import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

/**
 * Auto-start practice lab generation if missing labs detected
 * Runs on app startup for admin/staff users
 */
export const checkAndStartPracticeGeneration = async (userRole: string): Promise<void> => {
  // Only run for admin/staff
  if (!['admin', 'staff'].includes(userRole)) {
    return;
  }

  try {
    console.log('🔍 Checking for missing practice labs...');

    // Check if generation is already running
    const { data: existingOp } = await supabase
      .from('practice_generation_operations')
      .select('*')
      .eq('status', 'in_progress')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existingOp) {
      console.log('⏳ Practice lab generation already in progress:', existingOp.id);
      return;
    }

    // Check for missing labs
    const { data: stats } = await supabase
      .rpc('get_practice_lab_stats' as any)
      .single();

    if (!stats) return;

    const missingCount = (stats as any).total_modules - (stats as any).modules_with_labs;

    if (missingCount > 0) {
      console.log(`📊 Found ${missingCount} modules without practice labs`);
      console.log('🚀 Auto-starting practice lab generation...');

      // Start generation
      const { data, error } = await supabase.functions.invoke('bulk-generate-practice-labs', {
        body: { action: 'start' }
      });

      if (error) {
        console.error('❌ Failed to start practice generation:', error);
        toast.error('Failed to start practice lab generation');
        return;
      }

      console.log('✅ Practice lab generation started:', data);
      toast.success(`Started generating ${missingCount} missing practice labs`);
    } else {
      console.log('✅ All modules have practice labs');
    }
  } catch (error) {
    console.error('❌ Error in practice lab bootstrap:', error);
  }
};

/**
 * Get current generation progress
 */
export const getPracticeGenerationProgress = async (): Promise<{
  isRunning: boolean;
  progress: number;
  total: number;
  processed: number;
  successful: number;
  failed: number;
} | null> => {
  try {
    const { data: operation } = await supabase
      .from('practice_generation_operations')
      .select('*')
      .eq('status', 'in_progress')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!operation) {
      return null;
    }

    const progress = operation.total_modules > 0
      ? Math.round((operation.processed_modules / operation.total_modules) * 100)
      : 0;

    return {
      isRunning: true,
      progress,
      total: operation.total_modules,
      processed: operation.processed_modules,
      successful: operation.successful_modules,
      failed: operation.failed_modules
    };
  } catch (error) {
    console.error('❌ Error getting progress:', error);
    return null;
  }
};
